import clsx from 'clsx'
import PropTypes from 'prop-types'
import { useEffect, useState } from 'react'
import { toast } from 'react-toastify'
import { useTranslation } from 'react-i18next'

import Btn from '../../../../../../UI/Btn'

import SettingHint from '../SettingHint'

import { isTestEnvironment } from '../../../../../../shared/config'
import { showTicketsMenu, TICKETS_MENU_OPTION } from '../../../../../../shared/ticketsMenu'

import styles from '../style.module.scss'
import config from '../RequirementsConfig/style.module.scss'

/*
 * Der Menüpunkt „Anforderungen & Anfragen" — anzeigen oder nicht.
 *
 * Anlass: Anforderungen und Anfragen entstehen dort, wo gearbeitet wird — auf
 * der Produktion. Test- und Produktivumgebung haben getrennte Datenbestände;
 * was auf Test erfasst wird, sieht niemand, der es bearbeiten müsste. Der
 * Menüpunkt führte dort in eine Liste, in der nichts Echtes steht.
 *
 * Ohne gesetzte Einstellung entscheidet deshalb die Umgebung: Produktion
 * sichtbar, Testumgebung nicht. Diese Vorgabe übersteht auch das Einspielen
 * einer Datenkopie aus der Produktion — eine dort gesetzte Einstellung käme
 * damit sonst als „sichtbar" zurück.
 *
 * Ein eigenes Formular statt einer Zeile in einer bestehenden Liste: Es ändert
 * keine fachliche Regel, sondern was im Menü steht.
 */

function TicketsMenu(props) {
	const { t } = useTranslation()

	const [sichtbar, setSichtbar] = useState(() => showTicketsMenu(props.form))

	// Der Wert kommt nach, wenn die Einstellungen geladen sind.
	useEffect(() => setSichtbar(showTicketsMenu(props.form)), [props.form])

	const gesetzt = typeof props.form?.[TICKETS_MENU_OPTION] === 'boolean'

	const onSubmit = event => {
		event.preventDefault()

		props
			.updateOptions({ name: TICKETS_MENU_OPTION, value: sichtbar })
			.then(() => toast.success(t('Options was updated')))
	}

	return (
		<form id="ticketsMenu" className={styles.root} onSubmit={onSubmit}>
			<section className={styles.section}>
				<h3 className={styles.sectionTitle}>Menüpunkt „Anforderungen &amp; Anfragen"</h3>
				<SettingHint
					example="Beispiel: ausgeschaltet — dann fehlt der Menüpunkt in der Navigationsleiste und der gleichnamige Abschnitt auf dem Dashboard. Die Anforderungsverwaltung selbst bleibt bestehen und ist über ihre Adresse (/anforderungen) weiterhin erreichbar."
					warning="Diese Einstellung blendet nur an. Sie sperrt nichts: Wer die Adresse kennt oder einen Verweis aus einer E-Mail öffnet, kommt weiterhin hinein — und der Hilfe-Knopf unten rechts bleibt in jedem Fall, damit sich das Stellen einer Anfrage auch auf der Testumgebung durchspielen lässt."
				>
					Anforderungen und Anfragen entstehen dort, wo gearbeitet wird: auf der Produktion. Testumgebung und
					Produktion haben getrennte Datenbestände — was auf der Testumgebung erfasst wird, sieht niemand,
					der es bearbeiten müsste, und was auf der Produktion offen ist, steht dort nicht. Ohne
					ausdrückliche Einstellung entscheidet deshalb die Umgebung: auf der Produktion angezeigt, auf der
					Testumgebung nicht. Wer die Anforderungsverwaltung auf der Testumgebung prüfen möchte, schaltet den
					Punkt hier ein.
				</SettingHint>
				<div className={styles.sectionContent}>
					<label className={clsx(config.switch, styles.field)}>
						<input
							type="checkbox"
							checked={sichtbar}
							onChange={event => setSichtbar(event.target.checked)}
						/>
						<span className={config.track} aria-hidden="true" />
						<span className={config.state}>{sichtbar ? 'angezeigt' : 'ausgeblendet'}</span>
					</label>

					{/*
					 * Solange nichts gesetzt ist, ist der Schalter eine Anzeige
					 * der Umgebungsvorgabe und keine Entscheidung. Das gehört
					 * dazugeschrieben — sonst liest man ihn als gesetzten Wert.
					 */}
					{!gesetzt && (
						<p className={styles.hintLine}>
							Noch nichts eingestellt — es gilt die Vorgabe dieser Umgebung
							{isTestEnvironment() ? ' (Testumgebung: ausgeblendet)' : ' (Produktion: angezeigt)'}.
							Speichern legt den Wert ausdrücklich fest.
						</p>
					)}

					<div className={styles.btnContainer}>
						<Btn
							className={styles.btn}
							type="submit"
							icon="arrow"
							iconSize={{ w: 10, h: 10 }}
							iconColor="#fff"
						>
							{t('save')}
						</Btn>
					</div>
				</div>
			</section>
		</form>
	)
}

TicketsMenu.propTypes = {
	form: PropTypes.object.isRequired,
	updateOptions: PropTypes.func.isRequired
}

export default TicketsMenu
